import { useState, useMemo } from 'react'

import { useContentCategories } from '@/core/hooks/misc/useContentCategories'

export type FilterType = 'content_category'

export interface FilteredSection {
	id: string
	type: FilterType
	filterValue: number // content category id
	title: string
}

function sortByPosition<T extends { positionSchedules: number | null }>(
	items: T[]
): T[] {
	return items
		.map((item, index) => ({ item, index }))
		.sort((a, b) => {
			const aPos = a.item.positionSchedules
			const bPos = b.item.positionSchedules
			if (aPos === null && bPos === null) return a.index - b.index
			if (aPos === null) return 1
			if (bPos === null) return -1
			return aPos - bPos
		})
		.map(({ item }) => item)
}

export function useFilteredScheduleSections() {
	const { data: contentCategories = [] } = useContentCategories({
		config: {
			params: {
				per_page: 250,
			},
		},
	})

	const allSections = useMemo<FilteredSection[]>(() => {
		const filtered = contentCategories.filter((c) => c.showInSchedules)
		const sorted = sortByPosition(filtered)
		return sorted.map((category) => ({
			id: `content-category-${category.id}`,
			type: 'content_category' as FilterType,
			filterValue: category.id,
			title: category.getLocalizedName('en-US'),
		}))
	}, [contentCategories])

	const [visibleCount, setVisibleCount] = useState(3)

	const visibleSections = useMemo(() => {
		return allSections.slice(0, visibleCount)
	}, [allSections, visibleCount])

	const hasMore = visibleCount < allSections.length

	const loadMore = () => {
		setVisibleCount((prev) => Math.min(prev + 3, allSections.length))
	}

	return {
		visibleSections,
		hasMore,
		loadMore,
		totalSections: allSections.length,
	}
}
